import React, { useMemo, useState, useRef } from 'react';
import styles from './MaterialTable.module.scss';
import { Send, Boxes } from 'lucide-react';
import Tooltip from '../../UI/ToolTip/ToolTip';
import { useHorizontalDragScroll } from '../../../hooks/onboardingV2/useHorizontalDragScroll.ts';

const MaterialTable = ({
  materials = [],
  onMaterialsChange,
  setShowSummary,
  isMyQuotation,
  totals,
  isEditable = true
}) => {
  const tableDragScroll = useHorizontalDragScroll([], { wheel: false });
  const inputRefs = useRef(new Map());
  const [showToolTip, setShowToolTip] = useState({
    send: { show: false, text: 'Debes cotizar al menos 1 material para continuar' }
  });

  const handleTableDragMouseDown = (event) => {
    const el = event.target;
    if (el instanceof Element && el.closest('input, button, a, textarea, select')) {
      return;
    }
    tableDragScroll.onMouseDown(event);
  };

  const handleTooltip = (button) => {
    setShowToolTip((prevState) => ({
      ...prevState,
      [button]: { ...prevState[button], show: !prevState[button].show }
    }));
  };

  const handleInputChange = (categoryId, itemId, field, value) => {
    if (!isEditable) return;
    const updatedMaterials = materials.map((category) => {
      if (category.category_id === categoryId) {
        return {
          ...category,
          materials: category.materials.map((item) =>
            item.id === itemId ? { ...item, [field]: value } : item
          )
        };
      }
      return category;
    });
    onMaterialsChange(updatedMaterials);
  };

  const formatNumber = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const sanitized = String(value).replace(',', '.');
    const numberValue = Number(sanitized);
    if (Number.isNaN(numberValue)) return '';
    return numberValue.toFixed(2);
  };

  const focusCell = (categoryId, itemId, field) => {
    const key = `${categoryId}-${itemId}-${field}`;
    const targetRef = inputRefs.current.get(key);
    if (targetRef && targetRef.focus) {
      targetRef.focus();
      if (targetRef.select) {
        targetRef.select();
      }
    }
  };

  const moveFocus = (categoryId, itemId, field, direction) => {
    const categoryIndex = materials.findIndex((category) => category.category_id === categoryId);
    if (categoryIndex === -1) return;
    const itemIndex = materials[categoryIndex].materials.findIndex((material) => material.id === itemId);
    if (itemIndex === -1) return;

    let nextCategoryIndex = categoryIndex;
    let nextItemIndex = itemIndex;
    let nextField = field;

    if (direction === 'nextField') {
      nextField = field === 'priceUnit' ? 'discount' : 'priceUnit';
    } else if (direction === 'prevField') {
      nextField = field === 'discount' ? 'priceUnit' : 'discount';
    } else if (direction === 'down' || direction === 'enter') {
      nextItemIndex = itemIndex + 1;
      nextField = direction === 'enter' ? 'priceUnit' : field;
    } else if (direction === 'up') {
      nextItemIndex = itemIndex - 1;
    }

    if (nextItemIndex < 0) {
      nextCategoryIndex = categoryIndex - 1;
      if (nextCategoryIndex >= 0) {
        const prevCategory = materials[nextCategoryIndex];
        nextItemIndex = prevCategory.materials.length - 1;
      }
    } else if (nextItemIndex >= materials[categoryIndex].materials.length) {
      nextCategoryIndex = categoryIndex + 1;
      nextItemIndex = 0;
    }

    if (nextCategoryIndex < 0 || nextCategoryIndex >= materials.length) return;

    const nextCategory = materials[nextCategoryIndex];
    const nextItem = nextCategory.materials[nextItemIndex];
    if (!nextItem) return;

    focusCell(nextCategory.category_id, nextItem.id, nextField);
  };

  const handleKeyDown = (event, categoryId, itemId, field) => {
    if (!isEditable) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      const direction = field === 'priceUnit' ? 'nextField' : 'enter';
      moveFocus(categoryId, itemId, field, direction);
    } else if (event.key === 'Tab') {
      const shift = event.shiftKey;
      const direction = shift ? (field === 'discount' ? 'prevField' : 'up') : field === 'priceUnit' ? 'nextField' : 'down';
      moveFocus(categoryId, itemId, field, direction);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveFocus(categoryId, itemId, field, 'down');
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveFocus(categoryId, itemId, field, 'up');
    } else if (event.key === 'ArrowLeft' && field === 'discount') {
      moveFocus(categoryId, itemId, field, 'prevField');
    } else if (event.key === 'ArrowRight' && field === 'priceUnit') {
      moveFocus(categoryId, itemId, field, 'nextField');
    }
  };

  const registerInput = (categoryId, itemId, field, element) => {
    const key = `${categoryId}-${itemId}-${field}`;
    if (element) {
      inputRefs.current.set(key, element);
    } else {
      inputRefs.current.delete(key);
    }
  };

  const formattedTotal = useMemo(() => {
    const totalAmount = materials.reduce((total, category) => {
      if (!Array.isArray(category.materials)) return total;
      return (
        total +
        category.materials.reduce((categoryTotal, item) => {
          const amount = Number(item.amount) || 0;
          const priceUnit = Number(item.priceUnit) || 0;
          const discount = Number(item.discount) || 0;
          const subtotal = amount * priceUnit * (1 - discount / 100);
          return categoryTotal + subtotal;
        }, 0)
      );
    }, 0);

    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(totalAmount);
  }, [materials]);

  return (
    <section className={styles.section}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <Boxes size={18} />
          </div>
          <div>
            <span className={styles.headerEyebrow}>Materiales a cotizar</span>
            <h2 className={styles.headerTitle}>Detalle de cantidades y precios</h2>
            {!isMyQuotation && (
              <p className={styles.headerSubtitle}>
                Podés cotizar la cantidad de materiales que quieras. No es necesario que todos estén
                completos para enviar tu presupuesto.
              </p>
            )}
          </div>
        </div>
        {!isMyQuotation && (
          <div
            className={styles.actionWrapper}
            onMouseEnter={() => handleTooltip('send')}
            onMouseLeave={() => handleTooltip('send')}
          >
            <button
              type="button"
              className={styles.actionButton}
              disabled={totals === 0}
              onClick={() => setShowSummary(true)}
            >
              <Send size={18} />
              {totals === 0 ? 'Cotiza al menos un material' : 'Continuar con el presupuesto'}
            </button>
            {totals === 0 && showToolTip.send.show && <Tooltip text={showToolTip.send.text} />}
          </div>
        )}
      </header>

      <div
        className={styles.tableContainer}
        ref={tableDragScroll.ref}
        onMouseDown={handleTableDragMouseDown}
        onMouseMove={tableDragScroll.onMouseMove}
        onMouseUp={tableDragScroll.onMouseUp}
        onMouseLeave={tableDragScroll.onMouseLeave}
      >
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Material</th>
              <th>Unidad</th>
              <th>Cantidad</th>
              <th>Precio unitario</th>
              <th>Descuento %</th>
              <th>IVA*</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {materials.map((category) => (
              <React.Fragment key={category.category_id}>
                <tr className={styles.categoryRow}>
                  <td colSpan={7}>{category.name}</td>
                </tr>
                {category?.materials?.map((item) => {
                  const unit = Number(item.priceUnit) || 0;
                  const amount = Number(item.amount) || 0;
                  const discount = Number(item.discount) || 0;
                  const subtotal = amount * unit * (1 - discount / 100);
                  const iva = unit * 0.21 || 0;
                  return (
                    <tr key={item.id}>
                      <td className={styles.materialName}>{item.name}</td>
                      <td>{item.unit || '---'}</td>
                      <td>{amount || '---'}</td>
                      <td>
                        {isEditable ? (
                          <input
                            type="number"
                            min="0"
                            step="1"
                            className={styles.input}
                            value={item.priceUnit || ''}
                            onChange={(e) =>
                              handleInputChange(
                                category.category_id,
                                item.id,
                                'priceUnit',
                                e.target.value
                              )
                            }
                            onBlur={(event) => {
                              const formatted = formatNumber(event.target.value);
                              handleInputChange(category.category_id, item.id, 'priceUnit', formatted);
                            }}
                            onKeyDown={(event) => handleKeyDown(event, category.category_id, item.id, 'priceUnit')}
                            ref={(element) => registerInput(category.category_id, item.id, 'priceUnit', element)}
                          />
                        ) : (
                          (unit && `$${unit.toFixed(2)}`) || '---'
                        )}
                      </td>
                      <td>
                        {isEditable ? (
                          <div className={styles.inputWithSuffix}>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              className={styles.input}
                              value={item.discount || ''}
                              onChange={(e) => {
                                handleInputChange(
                                  category.category_id,
                                  item.id,
                                  'discount',
                                  e.target.value
                                );
                              }}
                              onBlur={(event) => {
                                const formatted = formatNumber(event.target.value);
                                handleInputChange(category.category_id, item.id, 'discount', formatted);
                              }}
                              onKeyDown={(event) => handleKeyDown(event, category.category_id, item.id, 'discount')}
                              ref={(element) => registerInput(category.category_id, item.id, 'discount', element)}
                            />
                            <span className={styles.inputSuffix}>%</span>
                          </div>
                        ) : (
                          `${discount.toFixed(2)}%`
                        )}
                      </td>
                      <td>{`$${iva.toFixed(2)}`}</td>
                      <td>{`$${subtotal.toFixed(2)}`}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <footer className={styles.footer}>
        <span className={styles.footerNote}>* IVA (21%) opcional — completalo solo si corresponde</span>
        <div className={styles.footerTotal}>
          <span>Total cotización</span>
          <strong>{formattedTotal}</strong>
        </div>
      </footer>
    </section>
  );
};

export default MaterialTable;
